import type { TranslationMap } from "../lib/types.ts";

export const pt_BR: TranslationMap = {
  common: {
    health: "Saúde",
    ok: "OK",
    online: "Online",
    offline: "Offline",
    connect: "Conectar",
    refresh: "Atualizar",
    enabled: "Ativado",
    disabled: "Desativado",
    na: "n/a",
    version: "Versão",
    docs: "Docs",
    resources: "Recursos",
    search: "Pesquisar",
  },
  nav: {
    chat: "Chat",
    control: "Controle",
    agent: "Agente",
    settings: "Configurações",
    expand: "Expandir barra lateral",
    collapse: "Recolher barra lateral",
    resize: "Redimensionar barra lateral",
  },
  tabs: {
    agents: "Agentes",
    overview: "Visão Geral",
    channels: "Canais",
    instances: "Instâncias",
    sessions: "Sessões",
    usage: "Uso",
    cron: "Tarefas Cron",
    skills: "Habilidades",
    nodes: "Nós",
    chat: "Chat",
    config: "Config",
    communications: "Comunicações",
    appearance: "Aparência e Configuração",
    automation: "Automação",
    infrastructure: "Infraestrutura",
    aiAgents: "IA e Agentes",
    debug: "Debug",
    logs: "Logs",
  },
  subtitles: {
    agents: "Espaços, ferramentas, identidades.",
    overview: "Status, entrada, saúde.",
    channels: "Canais e configurações.",
    instances: "Clientes e nós conectados.",
    sessions: "Sessões ativas e padrões.",
    usage: "Uso e custos da API.",
    cron: "Despertares e execuções.",
    skills: "Habilidades e chaves API.",
    nodes: "Dispositivos e comandos.",
    chat: "Chat do gateway para intervenções rápidas.",
    config: "Editar openclaw.json.",
    communications: "Configurações de canais, mensagens e áudio.",
    appearance: "Configurações de tema, UI e assistente de configuração.",
    automation: "Configurações de comandos, hooks, cron e plugins.",
    infrastructure: "Configurações de gateway, web, browser e mídia.",
    aiAgents: "Configurações de agentes, modelos, habilidades, ferramentas, memória e sessão.",
    debug: "Snapshots, eventos, RPC.",
    logs: "Logs ao vivo do gateway.",
  },
  overview: {
    access: {
      title: "Acesso ao Gateway",
      subtitle: "Onde o dashboard se conecta e como ele se autentica.",
      wsUrl: "URL WebSocket",
      token: "Token do Gateway",
      password: "Senha (não armazenada)",
      sessionKey: "Chave de Sessão Padrão",
      language: "Idioma",
      connectHint: "Clique em Conectar para aplicar as alterações de conexão.",
      trustedProxy: "Autenticado por proxy confiável.",
    },
    snapshot: {
      title: "Snapshot",
      subtitle: "Informações mais recentes do handshake do gateway.",
      status: "Status",
      uptime: "Tempo de Atividade",
      tickInterval: "Intervalo de Tick",
      lastChannelsRefresh: "Última Atualização de Canais",
      channelsHint: "Use Canais para vincular WhatsApp, Telegram, Discord, Signal ou iMessage.",
    },
    stats: {
      instances: "Instâncias",
      instancesHint: "Beacons de presença nos últimos 5 minutos.",
      sessions: "Sessões",
      sessionsHint: "Chaves de sessão recentes rastreadas pelo gateway.",
      cron: "Cron",
      cronNext: "Próximo despertar {time}",
    },
    notes: {
      title: "Notas",
      subtitle: "Lembretes rápidos para configurações de controle remoto.",
      tailscaleTitle: "Tailscale serve",
      tailscaleText:
        "Prefira o modo serve para manter o gateway em loopback com autenticação tailnet.",
      sessionTitle: "Higiene de sessão",
      sessionText: "Use /new ou sessions.patch para redefinir o contexto.",
      cronTitle: "Lembretes de Cron",
      cronText: "Use sessões isoladas para execuções recorrentes.",
    },
    auth: {
      required:
        "Este gateway requer autenticação. Adicione um token ou senha e clique em Conectar.",
      failed:
        "Falha na autenticação. Recopie uma URL com token usando {command}, ou atualize o token e clique em Conectar.",
    },
    pairing: {
      hint: "Este dispositivo precisa de aprovação de pareamento do host do gateway.",
      mobileHint:
        "No celular? Copie a URL completa (incluindo #token=...) executando openclaw dashboard --no-open no desktop.",
    },
    insecure: {
      hint: "Esta página é HTTP, então o navegador bloqueia a identidade do dispositivo. Use HTTPS (Tailscale Serve) ou abra {url} no host do gateway.",
      stayHttp: "Se você precisar permanecer em HTTP, defina {config} (apenas token).",
    },
    connection: {
      title: "Como conectar",
      step1: "Inicie o gateway na sua máquina host:",
      step2: "Obtenha uma URL do painel com token:",
      step3: "Cole a URL do WebSocket e o token acima, ou abra a URL com token diretamente.",
      step4: "Ou gere um token reutilizável:",
      docsHint: "Para acesso remoto, recomendamos o Tailscale Serve. ",
      docsLink: "Leia a documentação →",
    },
    cards: {
      cost: "Custo",
      skills: "Habilidades",
      recentSessions: "Sessões Recentes",
    },
    attention: {
      title: "Atenção",
    },
    eventLog: {
      title: "Log de Eventos",
    },
    logTail: {
      title: "Logs do Gateway",
    },
    quickActions: {
      newSession: "Nova Sessão",
      automation: "Automação",
      refreshAll: "Atualizar Tudo",
      terminal: "Terminal",
    },
    palette: {
      placeholder: "Digite um comando…",
      noResults: "Sem resultados",
    },
  },
  login: {
    subtitle: "Painel do Gateway",
    passwordPlaceholder: "opcional",
  },
  chat: {
    disconnected: "Desconectado do gateway.",
    refreshTitle: "Atualizar dados do chat",
    thinkingToggle: "Alternar saída de pensamento/trabalho do assistente",
    focusToggle: "Alternar modo de foco (ocultar barra lateral + cabeçalho da página)",
    hideCronSessions: "Ocultar sessões de cron",
    showCronSessions: "Mostrar sessões de cron",
    showCronSessionsHidden: "Mostrar sessões de cron ({count} ocultas)",
    onboardingDisabled: "Desativado durante a integração",
  },
  languages: {
    en: "English",
    zhCN: "简体中文 (Chinês Simplificado)",
    zhTW: "繁體中文 (Chinês Tradicional)",
    ptBR: "Português (Português Brasileiro)",
    de: "Deutsch (Alemão)",
    es: "Español (Espanhol)",
  },
};
